import {
  deletePublicPrelaunchCompanyPaymentDetail,
  deletePublicPrelaunchLessonDescription,
  deletePublicPrelaunchStudio,
  getPublicPrelaunchOrganizationFormByUniqueId,
} from '../../../../api/prelaunchOrganizationFormsApi'
import LinkButton from '../../../../components/link-button/link-button'
import PrelaunchLessonDescriptions from '../../../../components/prelaunch-organizations/prelaunch-lesson-descriptions/prelaunch-lesson-descriptions'
import PrelaunchStudios from '../../../../components/prelaunch-organizations/prelaunch-studios/prelaunch-studios'
import translate from '../../../../i18n'
import {
  IPublicPrelaunchOrganizationForm,
  PrelaunchConfirmationStatusEnum,
} from '../../../prelaunch-organization-forms-container/share-types'
import { Button, Checkbox, Col, Divider, notification, Popconfirm, Row, Space } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

const PrelaunchOrganizationForm = () => {
  const [prelaunchOrganizationForm, setPrelaunchOrganizationForm] =
    useState<IPublicPrelaunchOrganizationForm>()
  const [now, setNow] = useState(() => dayjs())
  const params = useParams()
  const uniqueId = params?.uniqueId

  const loadPrelaunchOrganizationForm = useCallback(() => {
    if (uniqueId) {
      getPublicPrelaunchOrganizationFormByUniqueId(uniqueId).then(response => {
        if (response) {
          setPrelaunchOrganizationForm(response)
        }
      })
    }
  }, [uniqueId])

  useEffect(() => {
    loadPrelaunchOrganizationForm()
  }, [loadPrelaunchOrganizationForm])

  useEffect(() => {
    const timer = window.setInterval(() => setNow(dayjs()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  if (!prelaunchOrganizationForm) {
    return null
  }

  const expiredAt = dayjs(prelaunchOrganizationForm.expiredAt)
  const isExpired = !expiredAt.isAfter(now)
  const remainingMilliseconds = Math.max(expiredAt.diff(now), 0)
  const remainingMinutes = Math.ceil(remainingMilliseconds / (60 * 1000))
  const remainingDays = Math.floor(remainingMinutes / (24 * 60))
  const remainingHours = Math.floor((remainingMinutes % (24 * 60)) / 60)
  const remainingMinutePart = remainingMinutes % 60
  const handleDelete = async (
    deleteRequest: () => Promise<unknown>,
    successMessage: string,
    errorMessage: string,
  ) => {
    try {
      await deleteRequest()
      notification.success({ message: successMessage })
      loadPrelaunchOrganizationForm()
    } catch (error: any) {
      notification.error({
        message: errorMessage,
        description: error?.message || translate('networkError'),
      })
    }
  }

  return (
    <>
      <Row gutter={16}>
        <Col xs={{ span: 24 }} sm={{ span: 12 }}>
          <h1>{prelaunchOrganizationForm.organizationName}</h1>
          <div style={{ color: isExpired ? '#cf1322' : '#d46b08', fontWeight: 600 }}>
            {isExpired
              ? '链接已失效'
              : `链接将于 ${expiredAt.format('YYYY-MM-DD HH:mm')} 失效（剩余 ${remainingDays}天 ${remainingHours}小时 ${remainingMinutePart}分钟）`}
          </div>
        </Col>
        {!isExpired && (
          <Col xs={{ span: 24 }} sm={{ span: 12 }}>
            <Space wrap>
              <LinkButton
                to={`/public/prelaunch-organization-form/${prelaunchOrganizationForm.uniqueId}/studios/${prelaunchOrganizationForm.organizationName}`}
                type="primary"
              >
                {translate('addStudios')}
              </LinkButton>
              <LinkButton
                to={`/public/prelaunch-organization-form/${prelaunchOrganizationForm.uniqueId}/lesson-descriptions/${prelaunchOrganizationForm.organizationName}`}
                type="primary"
              >
                {translate('addLessonDescriptions')}
              </LinkButton>
              {!prelaunchOrganizationForm.hasCompanyPaymentDetail && (
                <LinkButton
                  to={`/public/prelaunch-organization-form/${prelaunchOrganizationForm.uniqueId}/payment-details/${prelaunchOrganizationForm.organizationName}`}
                  type="primary"
                >
                  {translate('addCompanyPaymentDetail')}
                </LinkButton>
              )}
            </Space>
          </Col>
        )}
      </Row>

      <Divider />

      <Space className="table-space" orientation="vertical" size={16}>
        <Row align="middle">
          <Col xs={{ span: 24 }} sm={{ span: 12 }} className="left-col">
            <h2>{translate('studio')}</h2>
          </Col>
        </Row>

        <PrelaunchStudios
          isExpired={isExpired}
          uniqueId={prelaunchOrganizationForm.uniqueId}
          organizationName={prelaunchOrganizationForm.organizationName}
          data={prelaunchOrganizationForm.studios}
          onDelete={id =>
            handleDelete(
              () => deletePublicPrelaunchStudio(prelaunchOrganizationForm.uniqueId, id),
              '删除场馆成功',
              '删除场馆失败',
            )
          }
        />
      </Space>

      <Divider />

      <Space className="table-space" orientation="vertical" size={16}>
        <Row align="middle">
          <Col xs={{ span: 24 }} sm={{ span: 12 }} className="left-col">
            <h2>{translate('lessonDescription')}</h2>
          </Col>
        </Row>

        <PrelaunchLessonDescriptions
          isExpired={isExpired}
          uniqueId={prelaunchOrganizationForm.uniqueId}
          organizationName={prelaunchOrganizationForm.organizationName}
          data={prelaunchOrganizationForm.lessonDescriptions}
          onDelete={id =>
            handleDelete(
              () => deletePublicPrelaunchLessonDescription(prelaunchOrganizationForm.uniqueId, id),
              '删除课程模版成功',
              '删除课程模版失败',
            )
          }
        />
      </Space>
      <Divider />
      <Space className="table-space" orientation="vertical" size={16}>
        <Row align="middle">
          <Col xs={{ span: 24 }} sm={{ span: 12 }} className="left-col">
            <h2>{translate('companyPaymentDetail')}</h2>
          </Col>
        </Row>
        <Space>
          <Checkbox checked={prelaunchOrganizationForm.hasCompanyPaymentDetail}>
            {translate('filledPaymentInfo')}
          </Checkbox>
          {!isExpired &&
            prelaunchOrganizationForm.companyPaymentDetail?.status ===
              PrelaunchConfirmationStatusEnum.SUBMITTED && (
              <>
                <LinkButton
                  to={`/public/prelaunch-organization-form/${prelaunchOrganizationForm.uniqueId}/payment-details/${prelaunchOrganizationForm.companyPaymentDetail.id}/${prelaunchOrganizationForm.organizationName}/edit`}
                >
                  {translate('edit')}
                </LinkButton>
                <Popconfirm
                  title="删除对公收款帐户详情"
                  description="删除后不可恢复，确认删除吗？"
                  onConfirm={() =>
                    handleDelete(
                      () =>
                        deletePublicPrelaunchCompanyPaymentDetail(
                          prelaunchOrganizationForm.uniqueId,
                          prelaunchOrganizationForm.companyPaymentDetail!.id,
                        ),
                      '删除对公收款帐户详情成功',
                      '删除对公收款帐户详情失败',
                    )
                  }
                >
                  <Button type="link" danger>
                    {translate('delete')}
                  </Button>
                </Popconfirm>
              </>
            )}
        </Space>
      </Space>
    </>
  )
}

export default PrelaunchOrganizationForm
